import { useQueryClient } from '@tanstack/react-query';
import { Redirect, useRouter } from 'expo-router';
import { useEffect, useState } from 'react';
import { Platform, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { ElectronicAccountType } from '@/api/partners/bank-account/types';
import { useBankAccount } from '@/api/partners/bank-account/use-bank-account';
import { useDeleteBankAccount } from '@/api/partners/bank-account/use-delete-bank-account';
import { isDesktop } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import useSettingsStore from '@/stores/use-settings-store';
import { Button, Card, Text } from '@/ui';
import { Chip, ChipType } from '@/ui/chip';
import { ConfirmModal } from '@/ui/confirm-modal';
import { Dot } from '@/ui/icons/dot';
import { Loader } from '@/ui/loader';

const accountTypeLabels: Record<ElectronicAccountType, string> = {
  businessChecking: 'Business checking',
  businessSavings: 'Business savings',
  personalChecking: 'Personal checking',
  personalSavings: 'Personal savings',
};

function formatAddedDate(value?: string): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function BankAccount() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const queryClient = useQueryClient();

  const bankAccountTutorialSeen = useSettingsStore(
    (state) => state.bankAccountTutorialSeen
  );

  const { data, isLoading } = useBankAccount();
  const { mutateAsync: deleteBankAccount, isPending: isDeleting } =
    useDeleteBankAccount();

  const [removeModalVisible, setRemoveModalVisible] = useState(false);

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Partner Bank Account',
    });
  }, []);

  if (isLoading || !data) {
    return (
      <View style={styles.loaderContainer}>
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  if (data.status === 'none') {
    return (
      <Redirect
        href={
          bankAccountTutorialSeen
            ? '/partner/more/bank-account/connect'
            : '/partner/more/bank-account/tutorial'
        }
      />
    );
  }

  if (data.status === 'action_required') {
    return <Redirect href="/partner/more/bank-account/connect?error=true" />;
  }

  // pending_verification accounts stay on this screen and surface a chip +
  // "Verify Account" action rather than auto-redirecting to the verify flow.
  const isPendingVerification = data.status === 'pending_verification';

  const primaryActionText = isPendingVerification
    ? 'Verify Account'
    : 'Edit Account';

  const handlePrimaryAction = () => {
    router.push(
      isPendingVerification
        ? '/partner/more/bank-account/verify'
        : '/partner/more/bank-account/connect?edit=true'
    );
  };

  const handleRemove = async () => {
    try {
      await deleteBankAccount();

      await queryClient.invalidateQueries({
        queryKey: ['partner/bank-account'],
      });

      Toast.show({
        text1: 'Bank account removed',
        type: 'success',
      });

      setRemoveModalVisible(false);
    } catch (error) {
      console.error('Error removing bank account:', error);
      setRemoveModalVisible(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.body}>
        {Platform.OS === 'web' && isDesktop() ? (
          <View style={styles.desktopNav}>
            <Text type="title1">Bank Account</Text>
          </View>
        ) : null}

        <Card>
          <View style={styles.cardHeader}>
            <View style={styles.cardHeaderTop}>
              <Text type="bodyRegular" style={{ flexShrink: 1 }}>
                {data.bankName ?? 'Bank Account'}
              </Text>

              {isPendingVerification ? (
                <Chip
                  type={ChipType.PENDING}
                  icon={<Dot />}
                  text="Verify account"
                  iconWidth={8}
                  iconHeight={8}
                  style={{ flexShrink: 0 }}
                />
              ) : null}
            </View>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              {`•••• •••• •••• ${data.last4 ?? ''}`}
            </Text>
          </View>

          <View style={styles.cardBody}>
            <View style={styles.row}>
              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                Account type
              </Text>

              <Text type="bodyRegular">
                {data.accountType ? accountTypeLabels[data.accountType] : '-'}
              </Text>
            </View>

            <View style={styles.row}>
              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                Holder
              </Text>

              <Text type="bodyRegular">{data.holderName ?? '-'}</Text>
            </View>

            <View style={styles.row}>
              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                Added
              </Text>

              <Text type="bodyRegular">{formatAddedDate(data.addedAt)}</Text>
            </View>
          </View>
        </Card>

        <View style={styles.actionsDesktop}>
          <Button
            text="Remove Bank Account"
            type="link"
            onPress={() => setRemoveModalVisible(true)}
            textStyle={{ color: theme.colors.error }}
            style={{ width: 225 }}
          />

          <Button
            text={primaryActionText}
            type="primary"
            onPress={handlePrimaryAction}
            style={{ width: 160 }}
          />
        </View>
      </View>

      <View style={styles.actionsMobile}>
        <Button
          text={primaryActionText}
          type="primary"
          onPress={handlePrimaryAction}
        />

        <Button
          text="Remove Bank Account"
          type="link"
          onPress={() => setRemoveModalVisible(true)}
          textStyle={{ color: theme.colors.error }}
        />
      </View>

      <ConfirmModal
        isVisible={removeModalVisible}
        titleTx="partner.bankAccount.removeTitle"
        bodyTx="partner.bankAccount.removeBody"
        leftButton={{
          tx: 'partner.bankAccount.cancel',
          onPress: () => setRemoveModalVisible(false),
        }}
        rightButton={{
          tx: 'partner.bankAccount.remove',
          onPress: () => {
            if (!isDeleting) {
              handleRemove();
            }
          },
        }}
        onDismiss={() => setRemoveModalVisible(false)}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  loaderContainer: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background_1,
  },
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
  },
  body: {
    flexGrow: 1,
    alignSelf: 'center',
    width: {
      xs: '100%',
      md: 600,
    },
    paddingHorizontal: {
      xs: 16,
      md: 56,
    },
    paddingTop: {
      xs: 16,
      md: 56,
    },
    gap: 24,
  },
  desktopNav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    gap: 16,
  },
  cardHeader: {
    padding: 16,
    gap: 4,
    borderBottomWidth: 1,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderColor: theme.colors.card.default.borderColor,
    backgroundColor: theme.colors.card.hover.backgroundColor,
  },
  cardHeaderTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  cardBody: {
    padding: 16,
    gap: 12,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  actionsMobile: {
    display: {
      xs: 'flex',
      md: 'none',
    },
    padding: 16,
    gap: 8,
  },
  actionsDesktop: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: {
      xs: 'space-between',
      md: 'flex-end',
    },
    gap: 16,
  },
}));
